import {
  WorkflowDefinitionDocument,
  WorkflowNavigationItem,
  WorkflowNavigationSettings,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';

export const workflowMenuIcons = [
  { code: 'pi pi-sitemap', label: 'Process' },
  { code: 'pi pi-briefcase', label: 'Work' },
  { code: 'pi pi-file', label: 'Document' },
  { code: 'pi pi-folder', label: 'Folder' },
  { code: 'pi pi-calendar', label: 'Calendar' },
  { code: 'pi pi-users', label: 'People' },
  { code: 'pi pi-check-square', label: 'Checklist' },
  { code: 'pi pi-inbox', label: 'Inbox' },
  { code: 'pi pi-list', label: 'List' },
  { code: 'pi pi-chart-bar', label: 'Chart' },
  { code: 'pi pi-cog', label: 'Settings' },
  { code: 'pi pi-shield', label: 'Shield' },
] as const;

export interface WorkflowMenuRow extends WorkflowNavigationItem {
  dashboardLabel: string;
  stepLabel: string;
}

const identity = (item: { nodeId: string; dashboardId: string }) =>
  JSON.stringify([item.nodeId, item.dashboardId]);

/** Strict readers reject extensions here; retain unsupported evidence instead of dropping it. */
export function workflowNavigationShapeError(navigation: unknown): string | null {
  if (navigation == null) return null;
  if (typeof navigation !== 'object' || Array.isArray(navigation))
    return 'This saved menu has an unsupported format. Its original settings are retained.';
  const value = navigation as Record<string, unknown>;
  if (Object.keys(value).some(key => !['iconKey', 'items'].includes(key)) ||
    typeof value['iconKey'] !== 'string' || !Array.isArray(value['items']))
    return 'This saved menu contains unsupported settings. Its original configuration is retained.';
  const fields = ['nodeId', 'dashboardId', 'label', 'iconKey', 'sortOrder', 'visible'];
  if (value['items'].some((item: unknown) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return true;
    const row = item as Record<string, unknown>;
    return Object.keys(row).some(key => !fields.includes(key)) ||
      typeof row['nodeId'] !== 'string' || typeof row['dashboardId'] !== 'string' ||
      (row['label'] != null && typeof row['label'] !== 'string') ||
      (row['iconKey'] != null && typeof row['iconKey'] !== 'string') ||
      typeof row['sortOrder'] !== 'number' || typeof row['visible'] !== 'boolean';
  })) return 'This saved menu contains unsupported dashboard-link settings. Its original configuration is retained.';
  return null;
}

export function workflowMenuRows(
  document: WorkflowDefinitionDocument,
): WorkflowMenuRow[] {
  const items = document.navigation?.items ?? [];
  let defaultOrder = 0;
  return document.nodes
    .filter((node) => node.typeCode === 'human_step')
    .sort(
      (a, b) =>
        a.displayOrder - b.displayOrder ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    )
    .flatMap((node) =>
      (node.declaration?.dashboards ?? []).map((dashboard) => {
        const sortOrder = defaultOrder++;
        const saved = items.find(
          (item) =>
            item.nodeId === node.id && item.dashboardId === dashboard.id,
        );
        return {
          nodeId: node.id,
          dashboardId: dashboard.id,
          dashboardLabel: dashboard.label,
          stepLabel: node.displayName || node.stepCode || node.id,
          label: saved?.label ?? null,
          iconKey: saved?.iconKey ?? null,
          sortOrder: saved?.sortOrder ?? sortOrder,
          visible: saved?.visible ?? true,
        };
      }),
    )
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder || identity(a).localeCompare(identity(b)),
    );
}

export function workflowNavigationError(
  document: WorkflowDefinitionDocument,
  navigation: WorkflowNavigationSettings | null,
): string | null {
  if (navigation == null) return null;
  const shapeError = workflowNavigationShapeError(navigation);
  if (shapeError) return shapeError;
  const icons: readonly string[] = workflowMenuIcons.map((icon) => icon.code);
  if (!icons.includes(navigation.iconKey))
    return 'Choose an available workflow menu icon.';
  if (!Array.isArray(navigation.items) || navigation.items.length > 200)
    return 'Use at most 200 dashboard menu items.';
  const known = workflowMenuRows({ ...document, navigation: null });
  const keys = new Set<string>();
  for (const item of navigation.items) {
    const key = identity(item);
    if (keys.has(key))
      return 'Each dashboard can appear only once in this menu.';
    keys.add(key);
    if (known.filter((row) => identity(row) === key).length !== 1)
      return 'A configured dashboard is missing or ambiguous. Restore menu defaults to remove stale links.';
    if (
      item.label != null &&
      (typeof item.label !== 'string' ||
        item.label.trim().length > 120 ||
        Array.from(item.label).some(
          (character) =>
            character.charCodeAt(0) < 32 ||
            (character.charCodeAt(0) >= 127 && character.charCodeAt(0) <= 159),
        ))
    )
      return 'Use a menu label of up to 120 characters.';
    if (item.iconKey != null && !icons.includes(item.iconKey))
      return 'Choose an available dashboard menu icon.';
    if (
      !Number.isSafeInteger(item.sortOrder) ||
      item.sortOrder < 0 ||
      item.sortOrder > 1000000 ||
      typeof item.visible !== 'boolean'
    )
      return 'Choose a valid menu order and visibility for each dashboard.';
  }
  return null;
}

export function applyWorkflowNavigation(
  document: WorkflowDefinitionDocument,
  navigation: WorkflowNavigationSettings | null,
): WorkflowDefinitionDocument {
  const error = workflowNavigationError(document, navigation);
  if (error) throw new Error(error);
  return {
    ...document,
    payloadSchemaVersion: navigation
      ? requireWorkflowPayloadVersion(document.payloadSchemaVersion, '1.14')
      : document.payloadSchemaVersion,
    navigation: navigation
      ? {
          iconKey: navigation.iconKey,
          items: navigation.items.map((item) => ({
            ...item,
            label: item.label?.trim() || null,
            iconKey: item.iconKey || null,
          })),
        }
      : null,
  };
}

export function workflowMenuFingerprint(
  document: WorkflowDefinitionDocument,
): string {
  return JSON.stringify({
    workflowCode: document.workflowCode,
    navigation: document.navigation ?? null,
    dashboards: document.nodes
      .filter((node) => node.declaration?.dashboards?.length)
      .map((node) => ({
        id: node.id,
        displayOrder: node.displayOrder,
        dashboards: node.declaration?.dashboards?.map(
          (dashboard) => dashboard.id,
        ),
      })),
  });
}
